/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 17-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : Framework-agnostic types for the Trade Task Template Builder.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR    |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * 22300          | 17-Jul-2026 | Vincy R     | Initial creation
 * AB#32844       | 05-Oct-2026 | Vincy R     | dependencyRelations added
 */

// Mirrors the DB contract: every version row has status:'active' virtually always;
// `invalid:false` is the ONE flag marking the current/latest version in that scope.

export type TaskType =
  | '' | 'Manual Task' | 'Create BOM Task' | 'Create Estimate Task' | 'Approve Contract Task'
  | 'Customer Task' | 'Remainder Customer Task' | 'Create PO Task' | 'Approve PO Task'
  | 'Confirm PO Task' | 'Receive PO Task' | 'Dispatch PO Task' | 'Return PO Task'
  | 'Verify PO Task' | 'Install Task' | 'Payment Task'
  | 'Create Invoice Task' | 'Approve Invoice Task' | 'Payment Verification Task' | 'Return Task'
  | 'Thank You Card Task';

export interface TaskTypeMeta { value: TaskType; label: string; system: boolean; }

export const TASK_TYPES: TaskTypeMeta[] = [
  { value: '', label: 'Milestone / Phase marker', system: false },
  { value: 'Manual Task', label: 'Manual Task', system: false },
  { value: 'Create BOM Task', label: 'Create BOM Task', system: true },
  { value: 'Create Estimate Task', label: 'Create Estimate Task', system: true },
  { value: 'Approve Contract Task', label: 'Approve Contract Task', system: false },
  { value: 'Customer Task', label: 'Customer Task', system: false },
  { value: 'Remainder Customer Task', label: 'Reminder — Customer Task', system: true },
  { value: 'Create PO Task', label: 'Create PO Task', system: true },
  { value: 'Approve PO Task', label: 'Approve PO Task', system: false },
  { value: 'Confirm PO Task', label: 'Confirm PO Task', system: false },
  { value: 'Receive PO Task', label: 'Receive PO Task', system: false },
  { value: 'Dispatch PO Task', label: 'Dispatch PO Task', system: false },
  { value: 'Return PO Task', label: 'Return PO Task', system: false },
  { value: 'Verify PO Task', label: 'Verify PO Task', system: false },
  { value: 'Install Task', label: 'Install Task', system: false },
  { value: 'Payment Task', label: 'Payment Task', system: false },
  { value: 'Create Invoice Task', label: 'Create Invoice Task', system: true },
  { value: 'Approve Invoice Task', label: 'Approve Invoice Task', system: false },
  { value: 'Payment Verification Task', label: 'Payment Verification Task', system: false },
  { value: 'Return Task', label: 'Return Task', system: false },
  { value: 'Thank You Card Task', label: 'Thank You Card Task', system: false },
];

export type DependencyClass = 'HARD' | 'SOFT';

/**
 * Target ASE engine for a trade template. '2.0' is the full 21-field task contract (CP-SAT scheduling
 * attributes). '1.0' is the legacy 14-field shape — the builder hides, and the serializer omits, the
 * seven 2.0-only attributes: is_compressible, min_workers, max_workers, compression_factor, bom,
 * dependency_classes, is_system_task.
 */
export type AseVersion = '1.0' | '2.0';

/** Task attributes that exist only in ASE 2.0 templates (dropped for ASE 1.0). */
export const ASE2_ONLY_TASK_FIELDS = [
  'is_compressible', 'min_workers', 'max_workers', 'compression_factor',
  'bom', 'dependency_classes', 'is_system_task',
] as const;

export type PhaseId = 'sales' | 'preConstruction' | 'activeConstruction' | 'postConstruction';
export const PHASES: { id: PhaseId; label: string }[] = [
  { id: 'sales', label: 'Sales' },
  { id: 'preConstruction', label: 'Pre-Construction' },
  { id: 'activeConstruction', label: 'Active Construction' },
  { id: 'postConstruction', label: 'Post-Construction' },
];

export type ResourceType = '1099' | 'W2';
export const RESOURCE_TYPES: ResourceType[] = ['1099', 'W2'];
/** Default resource groups; the Inspector may widen this from a tenant source of truth. */
export const RESOURCE_GROUPS_DEFAULT: string[] = ['sales', 'labour', 'foreman', 'purchaseOfficer', 'shopSupervisor'];

export interface TaskResource { type: ResourceType; group: string; count: number; }
/**
 * `state` — 'On Completion', matching the two tenant-web editors (ActionTab.tsx/ActionForm.tsx);
 * this used to say 'On Complete' (design doc §5.1 item 2's "source convergence" hygiene fix,
 * 2026-08-08) — the backend TaskActionDispatcher tolerates BOTH spellings permanently for already-
 * saved rows, so this change only affects what NEW rows authored here save, never what fires.
 *
 * `automationPlanId` — design doc §10.1: the tenant plan uid for 'Start Automation Plan'/
 * 'End Automation Plan' rows. A structurally different reference than `item` (a plan id, not a
 * template id), so it gets its own field. Deliberately a plain string here, not a picker — a shared
 * trade template has no bound tenant/project at authoring time, so there is no real, resolvable
 * plan to pick from; this is a DEFAULT value copied onto the real task at creation time (§10.6),
 * which a tenant then edits per-project in ActionTab.tsx, where a real picker over their own forked
 * plans already exists.
 */
/**
 * automationPlanId vs automationPlanMasterUid (AB#26892/26913): `automationPlanId` is a TENANT's
 * own fork uid — different per tenant for "the same" conceptual plan, so it is only ever meaningful
 * on a tenant-specific template/project that already resolved it. `automationPlanMasterUid` is the
 * PROVIDER master's own stable uid — the same value for every tenant — and is what a SHARED
 * template (this builder's own scope: no bound tenant) must carry instead. ASE 2.0's
 * TradeTemplateLoader resolves a row's automationPlanMasterUid into the materializing tenant's own
 * automationPlanId at Create-Bid/Create-Estimate time; an already-set automationPlanId is left
 * untouched (backward compatible with rows authored before this field existed).
 */
export interface TaskAction { state: 'On Start' | 'On Completion'; action: string; item?: string; automationPlanId?: string; automationPlanMasterUid?: string; resolvePlanFromOwnMetaKey?: boolean; }
/**
 * `resolvePlanFromOwnMetaKey` — resolve-plan-from-metakey-feature-spec.md §2/§3: opt-in flag for a
 * 'Start Automation Plan'/'End Automation Plan' row only. When true, the tenant-side reconciler
 * prefers this task's own `additionalData.metaKey`'s current resolved value (a Populate-pattern
 * MethodDef the mapping author owns — e.g. an IF/ELSE returning a plan uid) over the row's static
 * `automationPlanMasterUid`, which becomes purely the fallback for when that resolved value is
 * empty. Generic by design — no MetaKey name is ever hardcoded here or in the reconciler; the
 * MetaKey itself stays 100% author-supplied data, same as every other MetaKey in this system.
 * Provider-web's only job for this flag is authoring it (TaskInspector's Actions accordion); the
 * resolution rule itself lives entirely in tenant-web (out of this file's scope).
 */
/** Curated action-type options for the Actions editor's dropdown. `action` itself is a free string
 *  (no backend enum), so this is presentation-only — an older task's action value that predates this
 *  list (e.g. a legacy "Send Contract") is still preserved in the task data even though it isn't one
 *  of these MenuItems; TaskInspector's Actions accordion injects it as its own (legacy-marked) option
 *  so it still displays instead of MUI's Select rendering blank for an unmatched value. */
export const TASK_ACTION_TYPES: string[] = [
  'Send SMS', 'Send eMail', 'Send Notification', 'Start Automation Plan', 'End Automation Plan',
  'Upload Item', 'Confirm/Reject', 'Attach Item',
];
/** One field-crew checklist sub-step under a task. `order` is display order — always renumbered
 *  sequentially by array position on save, so it can't drift from the UI's actual list order. */
export interface ChecklistItem { label: string; order: number; }

export interface Task {
  task_uid: string;            // WBS number, e.g. "1", "1.2", "1.2.3" — dot-notation IS the hierarchy
  parent_task_uid: string;     // derived from task_uid; empty string for top-level (phase root)
  taskName: string;
  phase: PhaseId;
  task_order: number;          // legacy numeric order key used by `dependencies`
  dependencies: number[];      // task_order values this task waits on
  /** Relation/lag suffix per dependency, e.g. { 23000: 'FS+40h' }. ASE's stored token is
   *  `<task_order><suffix>` ('23000FS+40h'); the numeric head drives the graph while the
   *  suffix is scheduling data the Builder must preserve but does not interpret. Keyed by
   *  dependency id so adding or removing a dependency can never desync the two. */
  dependencyRelations?: Record<number, string>;
  dependency_classes: Record<number, DependencyClass>;
  estimatedDays: number;
  estimatedHours: number;
  timeLine: string;            // legacy free-text schedule hint (usually ""); preserved round-trip
  taskType: TaskType;
  is_milestone: boolean;       // DERIVED — see isMilestoneUid(). Not user-settable.
  is_system_task: boolean;     // Authored flag, persisted verbatim. ASE reads it verbatim.
  is_compressible: boolean;
  min_workers: number;
  max_workers: number;
  compression_factor: number;
  resources: TaskResource[];
  actions: TaskAction[];
  bom: unknown[];              // Bill-of-materials rows; no editor per design, but preserved round-trip
  additionalData: Record<string, unknown>; // e.g. { metaKey, unit } — question-mapping + unit metadata
  checklistItems: ChecklistItem[]; // ordered field-crew sub-steps shown under the task; [] when none
  valid: boolean;              // false = soft-deleted / excluded from active view
  status: 'active' | 'inactive';
}

export interface ForkPointer { scope: 'provider' | 'global' | 'tenant'; tradeId?: string; version: string; tenantId?: string; }

export interface TemplateVersion {
  version: string;             // "v1", "v2", ... independent per scope
  note: string;
  when: string;                // display timestamp / ISO string in production
  tasks: Task[];
  forkedFrom: ForkPointer | null;
  status: 'active';            // per spec: stays 'active' for virtually every row
  invalid: boolean;            // false === the current/latest version in this scope
  versionGroupUid?: string | null; // backend version_group_uid — ties the per-phase rows together
}

export interface Tenant { id: string; name: string; }

/** `draft` is the trade's unpublished saved (status='draft') provider-default work-in-progress, if
 *  any — drives the Draft chip on the provider grid card, same as `TradeTenantScope.draft` does for
 *  a tenant card. */
export interface TradeProviderScope { versions: TemplateVersion[]; draft?: DraftInfo | null; }
/** Lightweight summary of a scope's saved (status='draft') work-in-progress — drives the "Draft" chip. */
export interface DraftInfo { taskCount: number; when?: string | null; }
/** `loaded` distinguishes "not fetched yet" (show loading) from "fetched, no published version"
 *  (show empty) — the By-tenant cards lazy-load versions when a tenant is selected. `draft` is the
 *  tenant's unpublished saved draft for the trade, if any. */
export interface TradeTenantScope { versions: TemplateVersion[]; loaded?: boolean; draft?: DraftInfo | null; }

export interface Trade {
  id: string;
  name: string;
  category: string;
  provider: TradeProviderScope | null; // null = no dedicated provider template; falls back to Global Default
  tenants: Record<string /* tenantId */, TradeTenantScope>;
}

export interface GlobalDefaultTemplate { name: 'Global Default Template'; versions: TemplateVersion[]; }

export type Scope = 'global' | 'provider' | 'tenant';
export interface ScopeRef { scope: Scope; tradeId: string | null; tenantId: string | null; }
